<template>
  <CDropdown
    v-if="pitcherInfo?.apiOrigin && pitcherInfo?.accessToken && launchDarkly.allow_ai_prompts_in_canvas_text"
    :on-clickoutside="handleClickOutside"
    :options="
      isLoading || aiActionResponse ? responseStreamingOverlay : aiAction ? subOptions[aiAction] : aiActionOptions
    "
    placement="bottom-end"
    :show="showDropdown"
    :themeOverrides="themeOverrides"
    @select="(s: string) => aiActionHandler(s)"
    @update:show="handleDropdownVisibilityChange"
  >
    <div>
      <CButton :themeOverrides="themeOverrides[selectedOverride]" @click="onClick">
        <template #icon>
          <CIcon faType="fas" icon="cat-space" :size="16" />
        </template>
      </CButton>
    </div>
  </CDropdown>
</template>

<script lang="tsx" setup>
import { ref, inject, computed, type ComputedRef, type StyleValue, type VNode } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Editor } from '@tiptap/vue-3'
import { type DropdownOption, NButton, NCheckbox, NInput } from 'naive-ui'
import { DOMSerializer, DOMParser } from 'prosemirror-model'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CDropdown from '@lib/components/CDropdown/CDropdown.vue'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import { sanitizeHtml } from '@lib/util/string'
import type { PitcherInfo } from '@lib/types/app'
import type { Call } from '@lib/types/call'
import useToast from '@lib/composables/useToast'
import { requestStream } from '@lib/util/network'
import useCanvas from '@canvas-builder/composables/useCanvas'
import { useFetchMostPopularFiles } from '@canvas-builder/composables/useFetchMostPopularFiles'
import type { User } from '@openapi'

interface Props {
  editor: Editor
  themeOverrides: { active: StyleValue; default: StyleValue }
}

const pitcherInfo = inject<ComputedRef<PitcherInfo>>(
  'pitcherInfo',
  computed(() => undefined),
)
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const myUser = inject<ComputedRef<User | null>>(
  'myUser',
  computed(() => null),
)

const inCall = inject<ComputedRef<boolean>>(
  'inCall',
  computed(() => false),
)
const call = inject<ComputedRef<Call | null>>(
  'call',
  computed(() => null),
)

const { toast } = useToast()
const { activeCanvas, isCRM, getEnv } = useCanvas()

const emit = defineEmits<{
  startLoading: []
  endLoading: []
}>()
const props = defineProps<Props>()

const { t } = useI18n()
const showDropdown = ref(false)
const isLoading = ref(false)
const aiAction = ref('')
const aiActionResponse = ref('')
const aiActionSelection = ref<{ from: number; to: number }>()
const selectedOverride = ref<'default' | 'active'>('default')

const editorIsEmpty = computed(
  () =>
    (props.editor.state.doc.childCount === 1 &&
      props.editor.state.doc.firstChild?.isTextblock &&
      (props.editor.state.doc.firstChild.content.size === 0 ||
        props.editor.state.doc.firstChild.textContent.trim().length === 0)) ??
    false,
)

const hasInputBeenFocused = ref(false)

const handleDropdownVisibilityChange = (visible: boolean) => {
  if (!visible && !hasInputBeenFocused.value) {
    setTimeout(() => {
      const input = document.querySelector('.custom-user-input-container textarea')
      if (input instanceof HTMLElement) {
        input.focus()
        hasInputBeenFocused.value = true
      }
    }, 0)
  } else if (visible) {
    // Reset the flag when the dropdown is closed (which is when 'visible' is true in this case)
    hasInputBeenFocused.value = false
  }
}

const { fetchMostPopularFiles } = useFetchMostPopularFiles()
const aiActionOptions = computed(() => [
  {
    disabled: editorIsEmpty.value,
    icon: () => <CIcon icon="globe" />,
    key: 'translate',
    label: t('canvasUI.CAiRichTextActions.translate'),
  },
  {
    disabled: editorIsEmpty.value,
    icon: () => <CIcon icon="user-tie" />,
    key: 'moreFormal',
    label: t('canvasUI.CAiRichTextActions.moreFormal'),
  },
  {
    disabled: editorIsEmpty.value,
    icon: () => <CIcon icon="face-sunglasses" />,
    key: 'lessFormal',
    label: t('canvasUI.CAiRichTextActions.lessFormal'),
  },
  {
    disabled: editorIsEmpty.value,
    icon: () => <CIcon icon="spell-check" />,
    key: 'fixSpellingAndGrammar',
    label: t('canvasUI.CAiRichTextActions.fixSpellingAndGrammar'),
  },
  {
    disabled: editorIsEmpty.value,
    icon: () => <CIcon icon="circle-minus" />,
    key: 'shorten',
    label: t('canvasUI.CAiRichTextActions.shorten'),
  },
  {
    disabled: editorIsEmpty.value,
    icon: () => <CIcon icon="list-ul" />,
    key: 'extend',
    label: t('canvasUI.CAiRichTextActions.extend'),
  },
  {
    type: 'divider',
    key: 'dividerBeforeGenerate',
  },
  {
    icon: () => <CIcon icon="calendar" />,
    key: 'meetingAgenda',
    label: t('canvasUI.CAiRichTextActions.generateMeetingAgenda'),
  },
  {
    type: 'divider',
    key: 'dividerBeforeCustom',
  },
  {
    icon: () => <CIcon icon="terminal" />,
    key: 'custom',
    label: t('canvasUI.CAiRichTextActions.custom'),
  },
])
const lastUsedPrompts = ref<Record<string, string>>({})

const createInputOption = (key: string, placeholder: string, extraElements?: () => VNode) => ({
  render: () => (
    <div class={`${key}-input-container custom-user-input-container p-2 w-[300px]`}>
      <NInput
        type="textarea"
        placeholder={placeholder}
        defaultValue={lastUsedPrompts.value[key] || ''}
        onKeydown={(e) => {
          if (e.key === 'Enter') {
            handleInputSubmit(key, e.target as HTMLTextAreaElement)
          }
          e.stopPropagation()
        }}
      />
      {extraElements && extraElements()}
      <div style={{ textAlign: 'left', marginTop: '10px' }}>
        <NButton
          type="primary"
          onClick={(e) => {
            const textarea = (e.target as HTMLElement).closest(`.${key}-input-container`)?.querySelector('textarea')
            if (textarea) {
              handleInputSubmit(key, textarea)
            }
          }}
        >
          {t('canvasUI.CAiRichTextActions.submitButtonLabel')}
        </NButton>
      </div>
    </div>
  ),
  type: 'render',
  key: `${key}-input`,
})

const handleInputSubmit = (key: string, inputElement: HTMLInputElement | HTMLTextAreaElement) => {
  const value = inputElement.value
  lastUsedPrompts.value[key] = value
  if (key === 'translate') {
    aiActionHandler('translate-input', { language: value })
  } else if (key === 'custom') {
    const inputContainer = inputElement.closest('.custom-input-container')
    const includeAccount = Boolean(inputContainer?.querySelector('.n-checkbox--checked[data-action="includeAccount"]'))
    const includeMeeting = Boolean(inputContainer?.querySelector('.n-checkbox--checked[data-action="includeMeeting"]'))
    const includeFiles = Boolean(inputContainer?.querySelector('.n-checkbox--checked[data-action="includeFiles"]'))
    aiActionHandler('custom-input', { customPrompt: value, includeAccount, includeMeeting, includeFiles })
  }
  aiAction.value = ''
}

const subOptions: Record<string, DropdownOption[]> = {
  translate: [createInputOption('translate', t('canvasUI.CAiRichTextActions.translateTo'))],
  custom: [
    createInputOption('custom', t('canvasUI.CAiRichTextActions.customPlaceholder'), () => (
      <div class="text-left mt-2">
        {isCRM.value && (
          <NCheckbox
            default-checked={Boolean(activeCanvas.value?.account || inCall.value)}
            class="flex"
            data-action="includeAccount"
            disabled={!activeCanvas.value?.account && !inCall.value}
            label={t('canvasUI.CAiRichTextActions.includeAdditionalContext.account')}
          />
        )}
        {isCRM.value && (
          <NCheckbox
            default-checked={inCall.value}
            class="flex"
            data-action="includeMeeting"
            disabled={!inCall.value}
            label={t('canvasUI.CAiRichTextActions.includeAdditionalContext.meeting')}
          />
        )}
        <NCheckbox
          default-checked={true}
          class="flex"
          data-action="includeFiles"
          label={t('canvasUI.CAiRichTextActions.includeAdditionalContext.files')}
        />
      </div>
    )),
  ],
}

const responseStreamingOverlay: DropdownOption[] = [
  {
    render: () => (
      <div class="w-[500px] min-h-[200px] max-h-[400px] p-2 flex flex-col justify-between">
        <div
          class="flx-grow overflow-auto border border-solid border-primary5 flex-auto rounded-2 p-2"
          v-html={sanitizeHtml(aiActionResponse.value) || 'Loading...'}
        />
        <div class="mt-2 flex justify-end">
          <NButton type="primary" class="mr-2" disabled={isLoading.value} onClick={acceptResponse}>
            {t('canvasUI.CAiRichTextActions.acceptButton')}
          </NButton>
          <NButton onClick={isLoading.value ? cancelStreaming : hideAndReset}>
            {isLoading.value
              ? t('canvasUI.CAiRichTextActions.cancelButton')
              : t('canvasUI.CAiRichTextActions.rejectButton')}
          </NButton>
        </div>
      </div>
    ),
    type: 'render',
    key: 'response-streaming',
  },
]

function getHTMLContentBetween(editor: Editor, from: number, to: number) {
  const { doc } = editor.state
  const $from = doc.resolve(from)

  // If there's no selection (cursor position), expand to the entire document
  if (from === to) {
    // First try to expand to the current block
    let depth = $from.depth
    let foundBlock = false

    while (depth > 0) {
      const node = $from.node(depth)
      if (node.type.name === 'paragraph' || node.type.name === 'listItem') {
        from = $from.start(depth)
        to = $from.end(depth)
        foundBlock = true
        break
      }
      depth--
    }

    // If no suitable block was found or if we want to always send the entire content
    // when there's no selection, use the entire document
    if (!foundBlock) {
      from = 1 // Skip the first position (document start)
      to = doc.content.size - 1 // Skip the last position (document end)
    }
  }

  const slice = doc.slice(from, to)
  const serializer = DOMSerializer.fromSchema(editor.schema)
  const fragment = serializer.serializeFragment(slice.content)

  const tempDiv = document.createElement('div')
  tempDiv.appendChild(fragment)

  return {
    content: tempDiv.innerHTML,
    selection: { from, to },
  }
}

function onClick() {
  showDropdown.value = !showDropdown.value
  aiAction.value = ''
}

function reset() {
  aiAction.value = ''
  aiActionResponse.value = ''
  abortController.value?.abort()
  abortController.value = null
}

function hideAndReset() {
  showDropdown.value = false
  reset()
  // Ensure we clear any ongoing processes
  isLoading.value = false
  emit('endLoading')
}

function handleClickOutside() {
  if (!isLoading.value && !aiActionResponse.value && !aiAction.value) {
    showDropdown.value = false
    reset()
  }
}

interface FileForPrompt {
  id: string
  name: string
  keypoints: string[]
}

async function getRelevantFilesForPrompt(): Promise<FileForPrompt[]> {
  const files = await fetchMostPopularFiles()
  return files
    .map(
      (file): FileForPrompt => ({
        id: file.id,
        name: file.name ?? '',
        keypoints: file.metadata?.keypoints ?? [],
      }),
    )
    .filter((f) => f.name && f.keypoints?.length)
}

function filesToPrompt(files: FileForPrompt[]) {
  return files.map((f) => `${f.name} (ID: ${f.id}): ${JSON.stringify(f.keypoints)}`).join('\n')
}

function getCallDetailsForPrompt() {
  return call.value
    ? {
        selectedAccount: { ...call.value?.selectedAccount },
        selectedEvent: { ...call.value?.selectedEvent },
        selectedContacts: { ...call.value?.selectedContacts },
      }
    : null
}

const SIMPLE_PROMPT_ACTIONS = {
  moreFormal: 'Make the following more formal:',
  lessFormal: 'Make the following less formal:',
  fixSpellingAndGrammar: 'Fix spelling and grammar for the following:',
  shorten: 'Shorten by ~20% the following:',
  extend: 'Extend by ~20% the following:',
} as const

async function aiActionHandler(action: string, extra?: Record<string, any>) {
  const { selection } = props.editor.state

  // Get content - this will now return the entire document if there's no selection
  const { content, selection: updatedSelection } = getHTMLContentBetween(props.editor, selection.from, selection.to)

  // Check if selection was empty and now contains the entire document
  const isEntireDocument =
    selection.empty && updatedSelection.from === 1 && updatedSelection.to === props.editor.state.doc.content.size - 1

  // Add indicator if it's the entire document
  const selectedHtml = isEntireDocument
    ? `---START-USER-INPUT-ENTIRE-DOCUMENT--- ${content} ---END-USER-INPUT---`
    : `---START-USER-INPUT--- ${content} ---END-USER-INPUT---`

  hasInputBeenFocused.value = false
  const promptPrefix = `Only change the content not the html tags or structure. This will be embedded in a WYSIWYG editor, so only include very basic html tags, such as p, br, ul, ol, li, strong, em, u. Your answer should not include and other special markup or explanations. `

  aiAction.value = ''
  aiActionResponse.value = ''
  aiActionSelection.value = updatedSelection
  if (Object.hasOwn(SIMPLE_PROMPT_ACTIONS, action)) {
    await handleSimplePrompt(action as keyof typeof SIMPLE_PROMPT_ACTIONS, selectedHtml, promptPrefix)
  } else if (action === 'translate') {
    aiAction.value = action
  } else if (action === 'translate-input' && extra?.language) {
    await handleTranslation(extra.language, selectedHtml, promptPrefix)
  } else if (action === 'meetingAgenda') {
    await handleMeetingAgenda()
  } else if (action === 'custom') {
    aiAction.value = action
  } else if (action === 'custom-input' && extra?.customPrompt) {
    await handleCustomPrompt(extra, selectedHtml, promptPrefix)
  }
}

async function handleSimplePrompt(
  action: keyof typeof SIMPLE_PROMPT_ACTIONS,
  selectedHtml: string,
  promptPrefix: string,
) {
  reset()
  await prompt(promptPrefix + `${SIMPLE_PROMPT_ACTIONS[action]} ${selectedHtml}`)
}

async function handleTranslation(language: string, selectedHtml: string, promptPrefix: string) {
  reset()
  await prompt(promptPrefix + `Translate to ${language}: ${selectedHtml}`)
}

async function promptWithRetry(userPrompt: string, filesForPrompt: FileForPrompt[] = []) {
  try {
    await prompt(userPrompt)
  } catch (e: any) {
    if (e?.status === 400 || e?.statusCode === 400) {
      if (filesForPrompt.length > 0) {
        const halfFiles = filesForPrompt.slice(0, Math.ceil(filesForPrompt.length / 2))
        const updatedPrompt = userPrompt.replace(filesToPrompt(filesForPrompt), filesToPrompt(halfFiles))
        try {
          await prompt(updatedPrompt)
        } catch (retryError) {
          console.error('Error after retrying with fewer files:', retryError)
          toast.error(t('canvasUI.CAiRichTextActionErrors.requestTooLong'))
        }
      } else {
        console.error('Request too long error')
        toast.error(t('canvasUI.CAiRichTextActionErrors.requestTooLong'))
      }
    } else {
      console.error('Error in promptWithRetry:', e)
      toast.error(t('canvasUI.CAiRichTextActionErrors.aiRequestFailed'))
    }
  }
}

async function handleMeetingAgenda() {
  const filesForPrompt = await getRelevantFilesForPrompt()
  const meeting = getCallDetailsForPrompt()
  const accountName = call.value?.selectedAccount?.Name || activeCanvas.value?.account?.name
  const enrichedFiles = filesToPrompt(filesForPrompt)
  const env = await getEnv?.value?.()
  const instanceName = env?.pitcher.instance.name || ''
  const userPrompt = `My name is ${myUser.value?.first_name} ${myUser.value?.last_name}, I'll have a meeting with a customer called ${accountName} selling ${instanceName} products. Generate a meeting agenda for ${meeting?.selectedEvent?.DurationInMinutes || '60 minutes'}. These are the sales material files I can use with key points of each file: ${enrichedFiles}. Can you create a meeting agenda also suggesting what content to use? Please respond in HTML format so I can put the response in a div. Only include very basic html tags, such as p, br, ul, ol, li, strong, em, u. Do not include anything else like head etc. I just need the div contents. In Div, when referring to files please include them with the link format <a data-type="content-link" data-text="{file.name}" href="/browser/content?file={file.id}">{file.name}</a>.`

  await promptWithRetry(userPrompt, filesForPrompt)
}

async function handleCustomPrompt(extra: Record<string, any>, selectedHtml: string, promptPrefix: string) {
  reset()
  // Make sure to include the selection in the prompt
  const selection = props.editor.state.selection
  // Always include the selection in the API call
  aiActionSelection.value = { from: selection.from, to: selection.to }

  let userPrompt = `${promptPrefix}\n\nAction: ${extra.customPrompt}\n\nContent: ${selectedHtml}`
  let filesForPrompt: FileForPrompt[] = []

  if (extra.includeAccount) {
    const account = call.value?.selectedAccount || activeCanvas.value?.account
    if (account) userPrompt += `\n\nAccount: ${JSON.stringify(account)}`
  }
  if (extra.includeMeeting && inCall.value && call.value) {
    const meeting = getCallDetailsForPrompt()
    userPrompt += `\n\nMeeting: ${JSON.stringify(meeting)}`
  }
  if (extra.includeFiles) {
    filesForPrompt = await getRelevantFilesForPrompt()
    userPrompt += `\n\nFiles: ${filesToPrompt(filesForPrompt)}`
  }

  await promptWithRetry(userPrompt, filesForPrompt)
}

function acceptResponse() {
  const response = aiActionResponse.value
  const selection = aiActionSelection.value
  if (response && selection) {
    props.editor
      .chain()
      .focus()
      .command(({ tr, dispatch }) => {
        if (dispatch) {
          const { from, to } = selection

          // Parse the response HTML
          const parser = DOMParser.fromSchema(props.editor.schema)
          const tempDiv = document.createElement('div')
          tempDiv.innerHTML = sanitizeHtml(response)
          const slice = parser.parseSlice(tempDiv, {
            context: tr.doc.resolve(from),
          })

          // Replace the content
          tr.replaceRange(from, to, slice)

          // Clean up any unnecessary nodes
          const $from = tr.doc.resolve(from)
          const $to = tr.doc.resolve(tr.mapping.map(to))
          if ($from.parent.type !== $to.parent.type) {
            tr.join($to.pos)
          }
        }
        return true
      })
      .run()
  }
  hideAndReset()
}

const abortController = ref<AbortController | null>(null)

function cancelStreaming() {
  abortController.value?.abort()
  toast.info(t('canvasUI.CAiRichTextActions.streamingCancelled'))

  hideAndReset()
}

async function prompt(text: string) {
  if (!pitcherInfo.value?.apiOrigin || !pitcherInfo.value?.accessToken) {
    return Promise.reject(new Error('Missing required pitcher info. Cannot prompt.'))
  }

  emit('startLoading')
  isLoading.value = true
  abortController.value = new AbortController()
  try {
    console.log('Sending AI prompt', text)
    // Get the text generation model from LaunchDarkly flag or use default
    const textModel = launchDarkly.value.ai_text_generation_model || 'anthropic/claude-4-sonnet'

    const result = await requestStream(
      `${import.meta.env.DEV ? '' : pitcherInfo.value.apiOrigin}/core/api/protected/ai/ai-generation/stream`,
      {
        version: textModel,
        stream: true,
        input: {
          prompt: text,
          temperature: 0.7,
          max_tokens: 1024,
        },
      },
      pitcherInfo.value.accessToken,
      (chunk, response) => {
        try {
          // Handle streaming chunks from new API format
          const lines = chunk.split('\n').filter(Boolean)
          let fullOutput = ''
          let isCompleted = false

          for (const line of lines) {
            const data = JSON.parse(line)

            // Check if this is the final completed response
            if (data.status === 'succeeded' && data.output) {
              isCompleted = true
              // If the final output is an array, join it
              if (Array.isArray(data.output)) {
                fullOutput = data.output.join('')
              } else {
                fullOutput = data.output
              }
              break
            }

            // For streaming chunks, append to our accumulated output
            if (data.output && !isCompleted) {
              fullOutput += data.output
            }
          }

          // Only update the display if we have content
          if (fullOutput) {
            // For the UI display, we need to handle line breaks
            const formattedOutput = fullOutput.replace(/(?<!>)\n(?!<)/g, '<br />')
            aiActionResponse.value = formattedOutput
          }
        } catch (err) {
          // If parsing fails, fallback to old behavior
          aiActionResponse.value = response.replace(/(?<!>)\n(?!<)/g, '<br />')
        }
      },
      abortController.value.signal,
    )
    return result.replace(/(?<!>)\n(?!<)/g, '<br />')
  } catch (e: unknown) {
    if ((e as any)?.name === 'AbortError') {
      console.log('Streaming was cancelled')
      return
    }
    console.warn('Prompt failed: ', e)
    const statusMessage: string | undefined = (e as any)?.response?.data?.statusMessage
    if (statusMessage === 'request_too_long') {
      toast.error(t('canvasUI.CAiRichTextActionErrors.requestTooLong'))
    } else {
      toast.error(t('canvasUI.CAiRichTextActionErrors.aiRequestFailed'))
    }
    // Rethrow the error to handle in the place where the request was initiated
    throw e
  } finally {
    emit('endLoading')
    isLoading.value = false
    abortController.value = null
  }
}
</script>
